<!--
  @TradePanel  Left panel of the page "Handeln" (player view): cash of the hero, source filter and wish list (Merkliste).
-->

<script>
		import {user, view, send_data} from "../client.svelte.js";
		import {initials, money_label} from "../core.js";
		import {shop, shopSources, marked_entries, toggle_mark} from "../play/Shop.svelte";

		let gold = $derived(user.possessions.get(`${view.character?.ID}$gold`)?.amount ?? 0)
		let silver = $derived(user.possessions.get(`${view.character?.ID}$silver`)?.amount ?? 0)
		let cash = $derived(gold * 100 + silver)

		/** marked possessions with item, source and price, grouped by source **/
		let groups = $derived(Object.groupBy(marked_entries(view.character), entry => entry.source))
		let markedCount = $derived(Object.values(groups).reduce((sum, entries) => sum + entries.length, 0))
		let total = $derived(['merchant', 'companion'].reduce((sum, source) => sum + groups_total(source), 0))
		let remaining = $derived(cash - total)
		let over = $derived(remaining < 0)
		let budgetPct = $derived((cash > 0) ? Math.min(100, Math.round(total / cash * 100)) : (total > 0) ? 100 : 0)

		const groupHeads = {
				merchant:  "Beim Händler kaufen",
				found:     "Fundstücke an mich nehmen",
				companion: "Von Gefährten erhandeln" }

		function groups_total(source) {
				return (groups[source] ?? []).reduce((sum, entry) => sum + entry.price * (entry.poss.delta || 1), 0)
		}

		function pick_source(source) {
				shop.source = (shop.source === source) ? null : source
		}

		function trade_items() {
				send_data(`trade/${view.character.ID}`, {}, 'POST')
		}

		function clear_marks() {
				for (const entries of Object.values(groups))
						for (const entry of entries)
								toggle_mark(entry.item, entry.poss)
		}
</script>


<!-------------------------------- HTML -------------------------------------->

<div class="shopPanel">

	<section class="card">
		<div class="hero">
			<span class="avatar" aria-hidden="true">{initials(view.character?.name)}</span>
			<div>
				<p class="kicker">Bargeld von</p>
				<p class="name">{view.character?.name}</p>
			</div>
		</div>
		<div class="coins" data-help="cash">
			<div class="coin">
				<span class="kicker"><span class="dot gold"></span>Gold</span>
				<span class="num">{gold}</span>
			</div>
			<div class="coin">
				<span class="kicker"><span class="dot silver"></span>Silber</span>
				<span class="num">{silver}</span>
			</div>
		</div>
	</section>

	<section class="card" aria-labelledby="shop-sources">
		<h2 id="shop-sources">Anbieter</h2>
		<div class="segments" role="group" aria-label="Anbieter">
			{#each Object.entries(shopSources) as [source, {label}]}
				<button aria-pressed={shop.source === source} onclick={() => pick_source(source)}>{label}</button>
			{/each}
		</div>
	</section>

	<section class="card" aria-label="Merkliste">
		<div class="listhead">
			<h2>Merkliste</h2>
			<span class="muted">{markedCount} vorgemerkt</span>
		</div>

		{#each Object.keys(groupHeads) as source}
			{@render group(source)}
		{/each}

		<div class="budget" role="progressbar" aria-label="Anteil des Bargelds auf der Merkliste"
		     aria-valuemin="0" aria-valuemax="100" aria-valuenow={budgetPct}>
			<div class="fill" class:over style:width="{budgetPct}%"></div>
		</div>
		<div class="remaining">
			<span>Danach voraussichtlich verbleibend</span>
			<span class="sum" class:over>{money_label(remaining)}</span>
		</div>
		{#if over}
			<p class="warning">Das Bargeld reicht nicht für alle vorgemerkten Gegenstände.</p>
		{/if}

		<div class="actions">
			<button class="primary" disabled={markedCount === 0 || over} onclick={trade_items}>Kaufen & Anfragen senden</button>
			<button class="secondary" disabled={markedCount === 0} onclick={clear_marks}>Leeren</button>
		</div>
	</section>

</div>

{#snippet group(source)}
	{@const entries = (groups[source] ?? []).toSorted((a, b) => a.item.name.localeCompare(b.item.name, 'de'))}
	<div class="group" class:companion={source === 'companion'}>
		<div class="grouphead">
			<span class="kicker">{groupHeads[source]} · {entries.length}</span>
			{#if source === 'found'}
				<span class="muted">kostenlos</span>
			{:else}
				<span class="sum">{money_label(groups_total(source))}</span>
			{/if}
		</div>
		{#if entries.length === 0}
			<p class="nothing">Nichts vorgemerkt.</p>
		{:else}
			<ul>
				{#each entries as {item, poss, price} (poss.ID)}
					<li>
						<span class="itemname">{item.name}{#if poss.delta > 1} ({poss.delta}×){/if}</span>
						<span class="price">{(source === 'found') ? "frei" : money_label(price * (poss.delta || 1))}</span>
						<button class="remove" aria-label="Von der Merkliste nehmen: {item.name}" title="Von der Merkliste nehmen: {item.name}"
						        onclick={() => toggle_mark(item, poss)}>
							<i class="fa icon-xmark"></i>
						</button>
					</li>
				{/each}
			</ul>
		{/if}
	</div>
{/snippet}


<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	/* starts below the main menu button (which reaches into the control area of the player) */
	.shopPanel
		display: flex
		flex-direction: column
		gap: 12px
		margin-top: 52px
		max-height: calc(100vh - 116px)
		overflow-y: auto
		scrollbar-width: none

	.card
		display: flex
		flex-direction: column
		gap: 12px
		padding: 16px
		border: 1px solid colors.$grey-30
		border-radius: 10px
		background: colors.$grey-15

	h2
		margin: 0
		font-family: var(--font-heading, "EB Garamond", Georgia, serif)
		font-size: 18px

	.kicker
		display: flex
		align-items: center
		gap: 6px
		margin: 0
		font-size: 12px
		letter-spacing: .08em
		text-transform: uppercase
		color: colors.$grey-85

	.muted
		font-size: 13px
		color: colors.$grey-60

	/* — hero & cash — */
	.hero
		display: flex
		align-items: center
		gap: 12px
		line-height: 1.25

		.kicker
			color: colors.$primary

		.name
			margin: 0
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 20px
			font-weight: bold

	.avatar
		flex: none
		width: 44px
		height: 44px
		display: grid
		place-items: center
		border-radius: 50%
		border: 1px solid colors.$primary-100
		background: colors.$primary-040-black
		font-family: var(--font-heading, "EB Garamond", Georgia, serif)
		font-size: 17px
		font-weight: bold

	.coins
		display: grid
		grid-template-columns: repeat(2, minmax(0, 1fr))
		gap: 10px

	.coin
		padding: 10px 12px
		border: 1px solid colors.$grey-30
		border-radius: 8px
		background: #111111

		.num
			display: block
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 30px
			line-height: 1.1

	.dot
		width: 10px
		height: 10px
		border-radius: 50%

		&.gold
			background: colors.$highlight-yellow-100

		&.silver
			background: colors.$grey-85

	/* — source filter as segmented control — */
	.segments
		display: grid
		grid-template-columns: repeat(3, minmax(0, 1fr))
		border: 1px solid colors.$grey-30
		border-radius: 8px
		overflow: hidden

		button
			min-height: 44px
			margin: 0
			border: 0
			border-radius: 0
			background: #111111
			color: colors.$grey-85
			font-size: 14px
			cursor: pointer

			&[aria-pressed="true"]
				background: colors.$primary-100
				color: colors.$white
				font-weight: bold

			&:focus-visible
				outline: 2px solid colors.$primary
				outline-offset: -2px

	/* — wish list — */
	.listhead
		display: flex
		justify-content: space-between
		align-items: baseline

	.group
		display: flex
		flex-direction: column
		padding: 8px 4px 4px 12px
		border: 1px solid colors.$grey-20
		border-radius: 8px
		background: #111111

		&.companion .grouphead .kicker
			color: colors.$highlight-yellow-100

		ul
			margin: 0
			padding: 0
			list-style: none

		li
			display: flex
			align-items: center
			gap: 8px
			min-height: 44px

	.grouphead
		display: flex
		justify-content: space-between
		align-items: baseline
		padding: 0 8px 4px 0
		border-bottom: 1px solid colors.$grey-20

	.sum
		font-size: 14px
		font-weight: bold

	.nothing
		margin: 0
		padding: 10px 0
		font-size: 13px
		font-style: italic
		color: colors.$grey-60

	.itemname
		flex: 1
		min-width: 0
		font-size: 15px

	.price
		font-size: 14px
		color: colors.$grey-85
		white-space: nowrap

	.remove
		flex: none
		width: 44px
		height: 44px
		display: grid
		place-items: center
		margin: 0
		padding: 0
		border: 0
		border-radius: 6px
		background: transparent
		color: colors.$grey-60
		cursor: pointer

		&:hover
			color: colors.$white
			background: colors.$grey-20

		&:focus-visible
			outline: 2px solid colors.$primary
			outline-offset: -2px

	.budget
		height: 8px
		margin-top: 2px
		border-radius: 4px
		background: colors.$grey-20
		overflow: hidden

		.fill
			height: 100%
			background: colors.$primary-100

			&.over
				background: #e05252

	.remaining
		display: flex
		justify-content: space-between
		align-items: baseline
		font-size: 14px
		color: colors.$grey-85

		.sum
			font-size: 16px
			color: colors.$white

			&.over
				color: #e05252

	.warning
		margin: 0
		font-size: 13px
		color: #ff8a8a

	.actions
		display: flex
		gap: 8px
		margin-top: 4px

		button
			min-height: 44px
			margin: 0
			padding: 0 14px
			border-radius: 8px
			font-size: 14px
			cursor: pointer

			&:disabled
				cursor: not-allowed
				opacity: .5

			&:focus-visible
				outline: 2px solid colors.$primary
				outline-offset: 2px

		.primary
			flex: 1
			border: 1px solid colors.$primary-100
			background: colors.$primary-100
			color: colors.$white
			font-weight: bold

		.secondary
			border: 1px solid colors.$grey-50
			background: transparent
			color: colors.$grey-85
</style>
